import { ButtonPrimeReact } from "@finanedi.es/storybook"
import FormikAutoComplete from "common/components/FormikFields/FormikAutoComplete"
import FormikInputText from "common/components/FormikFields/FormikInputText"
import { DEFAULT_COUNTRY_SELECTED, countries } from "common/constants/countries"
import useRazonSocialAndCif from "common/hooks/useRazonSocialAndCif"
import useSearchEmpresa from "common/hooks/useSearchEmpresa"
import * as Validations from "common/utils/formValidations"
import { validateFormTabs } from "features/Contactos/constants"
import FormDatosBasicos from "features/Contactos/forms/DatosBasicos"
import FormEmailPhone from "features/Contactos/forms/EmailPhone"
import { resetApprovals } from "features/User/redux/slices/approvalSlices"
import { withFormik } from "formik"
import { useDispatch } from "hooks/useDispatch"
import { TFunction } from "i18next"
import { useEffect, useState } from "react"
import ReactFlagsSelect from "react-flags-select"
import { useTranslation } from "react-i18next"
import * as Yup from "yup"

const CrearContactoForm = ({
  values,
  setFieldValue,
  handleSubmit,
  onHide,
  isValid,
  dirty,
}: {
  values: {
    cif: string | null
    razonSocial: string | null
    poblacion: string | null
    poblacionId: number | null
    cp: string | number | null
    direccion: string | null
    codigoPais: string
    poblacionExtranjera: string | null
    email: string | null
    telefono: string | null
  }
  setFieldValue: (
    field: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: $TSMigrationFixMe,
    shouldValidate?: boolean
  ) => Promise<void>
  handleSubmit: () => void
  onHide: () => void
  isValid: boolean
  dirty: boolean
}) => {
  const { t } = useTranslation()
  const [tabSelected, setTabSelected] = useState(0)
  const dispatch = useDispatch()
  const {
    cifs,
    razonesSociales,
    getByCifOrRazonSocial,
    getRazonSocialByCif,
    getCifByRazonSocial,
  } = useRazonSocialAndCif()

  useEffect((): (() => void) => {
    return () => {
      return dispatch(resetApprovals())
    }
  }, [dispatch])

  const { searchingEmpresa, searchEmpresaByNif } = useSearchEmpresa({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSearchEmpresa: (empresa: $TSMigrationFixMe) => {
      setFieldValue("direccion", empresa.direccion?.calle)
      setFieldValue("cp", empresa.direccion?.codigoPostal)
      setFieldValue("poblacion", empresa.direccion?.poblacionNombre)
      setFieldValue("poblacionId", empresa.direccion?.poblacionId)
      setFieldValue("provinciaId", empresa.direccion?.provinciaId)
      setFieldValue("provincia", empresa.direccion?.provincia)
    },
  })

  const handleOnSelectCif = ({ value }: { value: string }) => {
    setTimeout(() => setFieldValue("cif", value), 50)
    const razonSocial = getRazonSocialByCif(value)
    setFieldValue("razonSocial", razonSocial)
    searchEmpresaByNif(value)
  }

  const handleOnSelectRazonSocial = ({ value }: { value: string }) => {
    setFieldValue("razonSocial", value)
    const cif = getCifByRazonSocial(value)
    setFieldValue("cif", cif)
    searchEmpresaByNif(cif)
  }

  const tabToRedirect = () => {
    const invalidTabs = validateFormTabs(values).filter(
      ({ invalid }) => invalid
    )

    const currentTabIsInvalid = invalidTabs.find(({ id }) => id === tabSelected)

    return currentTabIsInvalid || invalidTabs.length === 0
      ? tabSelected
      : invalidTabs[0].id
  }

  return (
    <div className='crear-contacto'>
      <form
        onSubmit={handleSubmit}
        className='w-100 d-flex flex-column'
      >
        <section>
          <div className='mt-3 text-left p-fluid p-formgrid'>
            <div className='p-field selected-country-mix-cif'>
              <label htmlFor='cif'>{t("contactos.crear.labelCif")}</label>
              <ReactFlagsSelect
                disabled={searchingEmpresa}
                className='selected-country-mix-cif__select-country-field'
                onSelect={(code) => setFieldValue("codigoPais", code)}
                searchable
                selected={values.codigoPais || DEFAULT_COUNTRY_SELECTED}
                customLabels={countries}
                searchPlaceholder={`${t("contactos.crear.placeholderCif")}`}
                showSelectedLabel={false}
              />

              <FormikAutoComplete
                disabled={searchingEmpresa}
                className='selected-country-mix-cif__input-cif-field'
                value={values?.cif}
                isCif
                id='cif'
                name='cif'
                completeMethod={(query: string) => {
                  getByCifOrRazonSocial("cif", query)
                }}
                suggestions={cifs}
                onSelect={handleOnSelectCif}
                autoFocus={undefined}
                maxLength={undefined}
                placeholder={undefined}
              />
            </div>

            <div className='p-field'>
              <label htmlFor='razonSocial'>
                {t("contactos.crear.labelCompanyName")}
              </label>

              {values.codigoPais === DEFAULT_COUNTRY_SELECTED ? (
                <FormikAutoComplete
                  disabled={searchingEmpresa}
                  id='razonSocial'
                  name='razonSocial'
                  completeMethod={(query: string) => {
                    getByCifOrRazonSocial("razonSocial", query)
                  }}
                  suggestions={razonesSociales}
                  onSelect={handleOnSelectRazonSocial}
                  autoFocus={undefined}
                  maxLength={undefined}
                  className={undefined}
                  placeholder={undefined}
                />
              ) : (
                <FormikInputText
                  disabled={searchingEmpresa}
                  id='razonSocial'
                  name='razonSocial'
                  placeholder={undefined}
                  autoFocus={undefined}
                  onChange={undefined}
                  className={undefined}
                  type={undefined}
                />
              )}
            </div>
          </div>

          <FormDatosBasicos
            values={values}
            setFieldValue={setFieldValue}
          />

          <FormEmailPhone />
        </section>

        <div
          onClick={() => setTabSelected(tabToRedirect())}
          className='mt-4 mr-1 row justify-content-end align-items-center'
        >
          <ButtonPrimeReact
            className='mr-3 large text'
            onClick={onHide}
          >
            {t("contactos.crear.labelCancel")}
          </ButtonPrimeReact>
          <ButtonPrimeReact
            type='submit'
            className='!tw-bg-secondary hover:!tw-bg-secondary-over !tw-border-none tw-cursor-pointer tw-py-3 tw-px-5'
            disabled={!(isValid && dirty)}
          >
            {t("contactos.crear.labelAddContact")}
          </ButtonPrimeReact>
        </div>
      </form>
    </div>
  )
}

export const validationSchema = (t: TFunction) =>
  Yup.object().shape({
    razonSocial: Validations.validateRazonSocial(t),
    cif: Yup.string()
      .when("codigoPais", {
        is: DEFAULT_COUNTRY_SELECTED,
        then: Validations.validateCif(t),
      })
      .required(`${t("errors.cifEmpty")}`),
    poblacion: Yup.string().when("codigoPais", {
      is: DEFAULT_COUNTRY_SELECTED,
      then: Validations.validatePoblacion(t),
    }),
    cp: Yup.string().when("codigoPais", {
      is: DEFAULT_COUNTRY_SELECTED,
      then: Validations.validateCodigoPostal(t),
    }),
    direccion: Validations.validateDireccion(t),
    email: Validations.validateEmail(t),
    telefono: Validations.validateTelefonoNotRequired(t),
  })

export const validateFn = (
  t: TFunction,
  {
    codigoPais,
    poblacionId,
  }: { codigoPais: null | string; poblacionId: null | number }
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const errors: $TSMigrationFixMe = {}

  if (!poblacionId && codigoPais === DEFAULT_COUNTRY_SELECTED) {
    errors.poblacionId = `${t("errors.selectedPoblation")}`
  }
  return errors
}

const FormCrearContactoForm = withFormik({
  validateOnMount: true,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mapPropsToValues: () => {
    return {
      cif: null,
      razonSocial: null,
      poblacion: null,
      poblacionId: null,
      cp: null,
      direccion: null,
      codigoPais: DEFAULT_COUNTRY_SELECTED,
      poblacionExtranjera: null,
      email: null,
      telefono: null,
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validate: (rowData, { t }: $TSMigrationFixMe) => validateFn(t, rowData),
  enableReinitialize: true,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validationSchema: ({ t }: $TSMigrationFixMe) => validationSchema(t),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleSubmit: async (values, { props }: $TSMigrationFixMe) => {
    props.updateData(values)
  },
})(CrearContactoForm)

export default FormCrearContactoForm
